// client/src/components/BulkQRSection.js
// client/src/components/BulkQRSection.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import qrcode from 'qrcode';

function BulkQRSection({ bulkGroups, onRefresh }) {
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [groupStats, setGroupStats] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false); // Added this state variable
  const navigate = useNavigate();
  

  // Toggle expanded view for a bulk group
  const toggleExpand = async (id) => {
    if (expandedGroupId === id) {
      setExpandedGroupId(null);
      setGroupStats(null);
    } else {
      setExpandedGroupId(id);
      setBaseUrl('');
      setError('');
      setSuccess('');
      
      // Fetch stats for this group when expanded
      try {
        setLoading(true);
        console.log('Fetching stats from URL:', `/api/bulk-qrcodes/${id}/stats`);
        const response = await fetch(`/api/bulk-qrcodes/${id}/stats`);
        
        if (response.ok) {
          const stats = await response.json();
          console.log('Stats received:', stats); 
          setGroupStats(stats);
        } else {
          console.error('Failed to fetch group stats, status:', response.status);
        }
      } catch (err) {
        console.error('Error fetching group stats:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle deleting a bulk group
  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bulk QR group? This will remove all QR codes in this group and cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/bulk-qrcodes/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccess('Bulk QR group deleted successfully');
        if (onRefresh) onRefresh();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete bulk QR group');
      }
    } catch (error) {
      setError('Error deleting bulk QR group: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle downloading bulk QR codes
  const handleDownloadGroup = async (id, name) => {
    try {
      setIsDownloading(true);
      
      // Fetch all QR codes in this group
      const response = await fetch(`/api/bulk-qrcodes/${id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch QR codes');
      }
      
      const bulkGroup = await response.json();
      const qrCodes = bulkGroup.qrCodes || [];
      
      if (!qrCodes || qrCodes.length === 0) {
        throw new Error('No QR codes found in this group');
      }
      
      // Create a zip file with all QR codes
      const zip = new JSZip();
      const folder = zip.folder(name || 'QR_Codes');
      
      // Create a hidden container for canvas elements
      const container = document.createElement('div');
      container.style.display = 'none';
      document.body.appendChild(container);
      
      // Process each QR code
      for (const qr of qrCodes) {
        // Create a high-resolution canvas
        const canvas = document.createElement('canvas');
        const size = 1200; // Large size for high resolution
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        // Get styling info from QR code or bulk group
        const shape = qr.shape || bulkGroup.shape || 'square';
        const bgColor = qr.backgroundColor || bulkGroup.backgroundColor || '#FFFFFF';
        const fgColor = qr.foregroundColor || bulkGroup.foregroundColor || '#000000';
        const textColor = qr.textColor || bulkGroup.textColor || '#000000';
        const dotStyle = qr.dotStyle || shape;
        const showName = qr.showName !== undefined ? qr.showName : bulkGroup.showName;
        const showURL = qr.showURL !== undefined ? qr.showURL : bulkGroup.showURL;
        
        // Draw background with shape
        ctx.fillStyle = bgColor;
        if (shape === 'circle') {
          ctx.beginPath();
          ctx.arc(size/2, size/2, size/2, 0, Math.PI * 2);
          ctx.fill();
        } else if (shape === 'rounded') {
          const radius = size / 10;
          ctx.beginPath();
          ctx.moveTo(radius, 0);
          ctx.lineTo(size - radius, 0);
          ctx.quadraticCurveTo(size, 0, size, radius);
          ctx.lineTo(size, size - radius);
          ctx.quadraticCurveTo(size, size, size - radius, size);
          ctx.lineTo(radius, size);
          ctx.quadraticCurveTo(0, size, 0, size - radius);
          ctx.lineTo(0, radius);
          ctx.quadraticCurveTo(0, 0, radius, 0);
          ctx.fill();
        } else {
          ctx.fillRect(0, 0, size, size);
        }
        
        // Generate QR code on a temporary canvas
        const tempCanvas = document.createElement('canvas');
        await new Promise((resolve) => {
          qrcode.toCanvas(
            tempCanvas, 
            `${window.location.origin}/${qr.shortId}`, // Important: Use shortId URL, not target URL
            {
              width: 200,
              margin: 0,
              color: {
                dark: fgColor,
                light: bgColor
              },
              errorCorrectionLevel: 'H'
            }, 
            () => resolve()
          );
        });
        
        // Get QR code data and draw with custom styling
        const tempCtx = tempCanvas.getContext('2d');
        const qrData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const data = qrData.data;
        const moduleCount = tempCanvas.width;
        
        // Calculate dot size based on canvas size
        const moduleSize = Math.floor(size * 0.7 / moduleCount);
        const startPoint = Math.floor((size - (moduleCount * moduleSize)) / 2);
        
        // Draw custom dots
        ctx.fillStyle = fgColor;
        for (let y = 0; y < moduleCount; y++) {
          for (let x = 0; x < moduleCount; x++) {
            const pixelIndex = (y * moduleCount + x) * 4;
            const isDark = data[pixelIndex] < 128;
            
            if (isDark) {
              const xPos = startPoint + x * moduleSize;
              const yPos = startPoint + y * moduleSize;
              
              if (dotStyle === 'circle') {
                ctx.beginPath();
                ctx.arc(
                  xPos + moduleSize / 2, 
                  yPos + moduleSize / 2, 
                  moduleSize / 2 * 0.9,
                  0, 
                  Math.PI * 2
                );
                ctx.fill();
              } else if (dotStyle === 'rounded') {
                const radius = moduleSize / 4;
                ctx.beginPath();
                ctx.moveTo(xPos + radius, yPos);
                ctx.lineTo(xPos + moduleSize - radius, yPos);
                ctx.quadraticCurveTo(xPos + moduleSize, yPos, xPos + moduleSize, yPos + radius);
                ctx.lineTo(xPos + moduleSize, yPos + moduleSize - radius);
                ctx.quadraticCurveTo(xPos + moduleSize, yPos + moduleSize, xPos + moduleSize - radius, yPos + moduleSize);
                ctx.lineTo(xPos + radius, yPos + moduleSize);
                ctx.quadraticCurveTo(xPos, yPos + moduleSize, xPos, yPos + moduleSize - radius);
                ctx.lineTo(xPos, yPos + radius);
                ctx.quadraticCurveTo(xPos, yPos, xPos + radius, yPos);
                ctx.fill();
              } else {
                ctx.fillRect(xPos, yPos, moduleSize, moduleSize);
              }
            }
          }
        }
        
        // Add logo if available
        if (qr.logo) {
          try {
            const logoImg = new Image();
            logoImg.crossOrigin = "Anonymous";
            
            await new Promise((resolve, reject) => {
              logoImg.onload = () => {
                const logoSize = size * 0.15;
                const logoX = (size - logoSize) / 2;
                const logoY = (size - logoSize) / 2;
                
                // Add background behind logo
                ctx.fillStyle = bgColor;
                ctx.fillRect(
                  logoX - size * 0.01, 
                  logoY - size * 0.01, 
                  logoSize + size * 0.02, 
                  logoSize + size * 0.02
                );
                
                // Draw logo
                ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
                resolve();
              };
              
              logoImg.onerror = () => {
                console.error("Error loading logo:", qr.logo);
                resolve();
              };
              
              logoImg.src = qr.logo;
            });
          } catch (logoError) {
            console.error("Error processing logo:", logoError);
          }
        }
        
        // Add text if enabled
        const fontFamily = "'Arial', sans-serif";
        
        if (showName && qr.qr_name) {
          const namePosition = size - 150;
          ctx.fillStyle = textColor;
          ctx.font = `bold ${Math.floor(size/20)}px ${fontFamily}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(qr.qr_name, size/2, namePosition);
        }
        
        if (showURL) {
          const urlPosition = showName && qr.qr_name ? size - 80 : size - 100;
          ctx.fillStyle = textColor;
          ctx.font = `${Math.floor(size/40)}px ${fontFamily}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`${window.location.origin}/${qr.shortId}`, size/2, urlPosition);
        }
        
        // Create filename
        let fileName = qr.qr_name ? 
          qr.qr_name.replace(/[^a-z0-9]/gi, '_') : 
          `qr_${qr._id}`;
        
        if (qr.slug) {
          fileName += `_${qr.slug.replace(/\W/g, '_')}`;
        }
        
        // Add to zip
        const dataUrl = canvas.toDataURL('image/png');
        const base64Data = dataUrl.split(',')[1];
        folder.file(`${fileName}.png`, base64Data, {base64: true});
      }
      
      // Clean up container
      document.body.removeChild(container);
      
      // Generate and download the ZIP file
      const zipContent = await zip.generateAsync({type: 'blob'});
      saveAs(zipContent, `${name || 'QR_Codes'}.zip`);
      
      setSuccess('QR codes downloaded successfully');
    } catch (error) {
      console.error('Error downloading QR code group:', error);
      setError('Error downloading QR codes: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle updating URLs for a bulk group
  const handleUpdateUrls = async (id) => {
    if (!baseUrl.trim()) {
      setError('Please enter a base URL');
      return;
    }

    try {
      setLoading(true);
      
      // Format the base URL if needed
      let formattedBaseUrl = baseUrl.trim();
      if (!formattedBaseUrl.startsWith('http://') && 
          !formattedBaseUrl.startsWith('https://') && 
          !formattedBaseUrl.startsWith('tel:') && 
          !formattedBaseUrl.startsWith('mailto:') && 
          !formattedBaseUrl.startsWith('sms:')) {
        formattedBaseUrl = 'https://' + formattedBaseUrl;
      }

      const response = await fetch(`/api/bulk-qrcodes/${id}/urls`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ baseUrl: formattedBaseUrl }),
      });

      if (response.ok) {
        // After updating URLs, fetch the updated stats to refresh the display
        try {
          const statsResponse = await fetch(`/api/bulk-qrcodes/${id}/stats`);
          if (statsResponse.ok) {
            const stats = await statsResponse.json();
            setGroupStats(stats);
          }
        } catch (statsErr) {
          console.error('Error refreshing stats after URL update:', statsErr);
        }

        setSuccess('QR code URLs updated successfully');
        setBaseUrl('');
        if (onRefresh) onRefresh();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update URLs');
      }
    } catch (error) {
      setError('Error updating URLs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to edit a bulk group
  const goToEditGroup = (id) => {
    navigate(`/bulk-edit/${id}`);
  };

  // View stats for a bulk group
  const viewStats = (id) => {
    navigate(`/bulk-stats/${id}`);
  };

  return (
    <div className="mt-6">
      <h2 className="text-xl font-semibold mb-4">Bulk QR Code Groups</h2>
      
      {bulkGroups.length === 0 ? (
        <div className="bg-gray-50 p-6 rounded-lg text-center">
          <p className="text-gray-600 mb-4">You haven't created any bulk QR code groups yet.</p>
          <Link to="/bulk-creator" className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-200">
            Create Bulk QR Codes
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {bulkGroups.map((group) => (
            <div key={group._id} className="border rounded-lg overflow-hidden shadow-sm">
              <div 
                className="bg-blue-100 px-4 py-3 flex justify-between items-center cursor-pointer"
                onClick={() => toggleExpand(group._id)}
              >
                <div className="flex items-center">
                  <div className="mr-3">
                    <span className={`transform transition-transform inline-block ${expandedGroupId === group._id ? 'rotate-90' : ''}`}>
                      ▶
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium">{group.name || 'Unnamed Group'}</h3>
                    <div className="flex space-x-4 text-sm text-gray-500">
                      <span>{group.count} QR codes</span>
                      <span>{new Date(group.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      viewStats(group._id);
                    }}
                  >
                    View Stats
                  </button>
                  <button 
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      goToEditGroup(group._id);
                    }}
                  >
                    Edit
                  </button>
                  <button 
                    className="text-green-600 hover:text-green-800 text-sm font-medium px-3 py-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadGroup(group._id, group.name);
                    }}
                    disabled={isDownloading}
                  >
                    {isDownloading ? 'Downloading...' : 'Download'}
                  </button>
                  <button 
                    className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGroup(group._id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              {expandedGroupId === group._id && (
                <div className="p-5 bg-white border-t">
                  {error && (
                    <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="bg-green-50 text-green-700 p-3 rounded-md mb-4">
                      {success}
                    </div>
                  )}
                  
                  {/* Stats section */}
                  {groupStats && (
                    <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-3">Group Stats</h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <div className="text-sm text-gray-500">Total Scans</div>
                          <div className="text-2xl font-bold">{groupStats.total || 0}</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <div className="text-sm text-gray-500">Today</div>
                          <div className="text-2xl font-bold">{groupStats.todayScans || 0}</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <div className="text-sm text-gray-500">This Week</div>
                          <div className="text-2xl font-bold">{groupStats.weekScans || 0}</div>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <div className="text-sm text-gray-500">This Month</div>
                          <div className="text-2xl font-bold">{groupStats.monthScans || 0}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* URL update section */}
                  <div className="mb-6">
                    <h4 className="font-medium mb-3">Update Base URL</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Update the base URL for all QR codes in this group. Individual slugs will remain unchanged.
                    </p>
                    
                    <div className="flex flex-col md:flex-row gap-3">
                      <input 
                        type="text"
                        value={baseUrl}
                        onChange={(e) => setBaseUrl(e.target.value)}
                        placeholder="Enter new base URL (e.g. example.com)"
                        className="flex-grow px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button 
                        onClick={() => handleUpdateUrls(group._id)}
                        disabled={loading || !baseUrl.trim()}
                        className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-200 ${
                          (loading || !baseUrl.trim()) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {loading ? 'Updating...' : 'Update URLs'}
                      </button>
                    </div>
                    
                    <div className="mt-4 text-sm bg-gray-50 p-3 rounded">
                      <p><strong>Example URL Construction:</strong></p>
                      <p className="mt-1"><strong>Base URL:</strong> <span className="font-mono">https://example.com</span></p>
                      <p><strong>Slug:</strong> <span className="font-mono">/product-1</span></p>
                      <p><strong>Final URL:</strong> <span className="font-mono">https://example.com/product-1</span></p>
                    </div>
                  </div>
                  
                  {/* Individual QR codes section */}
                  {groupStats && groupStats.qrCodes && groupStats.qrCodes.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3">Individual QR Codes</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="text-left py-2 px-3">Name</th>
                              <th className="text-left py-2 px-3">Target URL</th>
                              <th className="text-center py-2 px-3">Scans</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupStats.qrCodes.map(qr => (
                              <tr key={qr._id} className="border-t">
                                <td className="py-2 px-3">{qr.qr_name}</td>
                                <td className="py-2 px-3 font-mono text-xs break-all">
                                  {qr.targetUrl}
                                </td>
                                <td className="py-2 px-3 text-center">{qr.visits}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BulkQRSection;

// // client/src/components/BulkQRSection.js
// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import QRCode from 'qrcode.react';

// function BulkQRSection({ user }) {
//   const [bulkGroups, setBulkGroups] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');

//   useEffect(() => {
//     const fetchBulkGroups = async () => {
//       if (!user) return;

//       try {
//         const response = await fetch(`/api/bulk-qrcodes?user_id=${user.id}`);
        
//         if (!response.ok) {
//           throw new Error('Failed to fetch bulk QR code groups');
//         }
        
//         const data = await response.json();
//         setBulkGroups(data);
//         setLoading(false);
//       } catch (err) {
//         console.error('Error fetching bulk QR groups:', err);
//         setError(err.message || 'Failed to load bulk QR code groups');
//         setLoading(false);
//       }
//     };
    
//     fetchBulkGroups();
//   }, [user]);

//   if (loading) {
//     return <div className="text-center py-4">Loading bulk QR groups...</div>;
//   }

//   if (error) {
//     return <div className="bg-red-100 text-red-700 p-3 rounded-lg">{error}</div>;
//   }

//   if (bulkGroups.length === 0) {
//     return (
//       <div className="border rounded-lg p-6 mb-8 bg-gray-50">
//         <h2 className="text-xl font-semibold mb-3">Bulk QR Code Groups</h2>
//         <p className="text-gray-600 mb-4">You haven't created any bulk QR code groups yet.</p>
//         <Link 
//           to="/bulk-upload" 
//           className="inline-block bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700"
//         >
//           Create Bulk QR Codes
//         </Link>
//       </div>
//     );
//   }

//   return (
//     <div className="border rounded-lg overflow-hidden shadow-sm mb-8">
//       <div className="bg-blue-50 border-b border-blue-100 px-4 py-3">
//         <h2 className="text-xl font-semibold text-blue-800">Bulk QR Code Groups</h2>
//       </div>
      
//       <div className="divide-y">
//         {bulkGroups.map(group => (
//           <div key={group._id} className="p-4 hover:bg-gray-50">
//             <div className="flex flex-col sm:flex-row sm:items-center justify-between">
//               <div className="flex items-center mb-3 sm:mb-0">
//                 <div 
//                   className="w-12 h-12 mr-4 flex items-center justify-center rounded"
//                   style={{ backgroundColor: group.backgroundColor || '#FFFFFF' }}
//                 >
//                   <QRCode
//                     value={group.baseUrl || 'https://example.com'}
//                     size={40}
//                     fgColor={group.foregroundColor || '#000000'}
//                     bgColor={group.backgroundColor || '#FFFFFF'}
//                     level="L"
//                     renderAs="svg"
//                   />
//                 </div>
                
//                 <div>
//                   <h3 className="font-semibold text-lg">{group.name}</h3>
//                   <div className="text-sm text-gray-500 flex flex-wrap">
//                     <span className="mr-4">{group.qr_count || group.qrCodes?.length || 0} QR codes</span>
//                     <span className="mr-4">Created: {new Date(group.createdAt).toLocaleDateString()}</span>
//                   </div>
//                 </div>
//               </div>
              
//               <div className="flex space-x-3">
//                 <Link 
//                   to={`/edit-bulk/${group._id}`}
//                   className="px-3 py-1 border border-blue-500 text-blue-600 rounded hover:bg-blue-50"
//                 >
//                   Edit
//                 </Link>
//                 <a 
//                   href={`/api/bulk-qrcodes/${group._id}/download`}
//                   className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                 >
//                   Download
//                 </a>
//               </div>
//             </div>
            
//             {group.baseUrl && (
//               <div className="mt-2 text-sm text-gray-600">
//                 Base URL: <span className="font-mono">{group.baseUrl}</span>
//               </div>
//             )}
//           </div>
//         ))}
//       </div>
      
//       <div className="bg-gray-50 px-4 py-3 text-right">
//         <Link 
//           to="/bulk-upload" 
//           className="text-blue-600 hover:text-blue-800 font-medium"
//         >
//           Create New Bulk QR Group
//         </Link>
//       </div>
//     </div>
//   );
// }

// export default BulkQRSection;